import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import ZefoImport from './ZefoImport';
import LocationPicker, { locationLabel } from '../components/LocationPicker';

export default function SiteSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [health, setHealth] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [wpEditing, setWpEditing] = useState(false);
  const [wpSaving, setWpSaving] = useState(false);
  const [wpForm, setWpForm] = useState({ wp_api_url: '', wp_username: '', wp_app_password: '' });
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    business_description: '', business_niche: '', service_area: '', target_audience: '',
    usp: '', writing_style: '', writing_tone: '', always_include_words: '', never_use_words: '',
    brand_color_primary: '', brand_color_secondary: '',
  });
  const [logoUploading, setLogoUploading] = useState(false);

  // Paid advertising (פרסום ממומן)
  const [paidEditing, setPaidEditing] = useState(false);
  const [paidSaving, setPaidSaving] = useState(false);
  const [paidForm, setPaidForm] = useState({ site_type: '', paid_channels: [] });
  const [paidCatalog, setPaidCatalog] = useState({ site_types: [], channels: [] });
  const [customMetrics, setCustomMetrics] = useState({}); // {platform: [{key,label,type}]}
  const [newMetric, setNewMetric] = useState({}); // {platform: {label, type}}

  // Business results ("תוצאות עסקיות") — user-defined metric groups
  const [bizEditing, setBizEditing] = useState(false);
  const [bizSaving, setBizSaving] = useState(false);
  const [bizGroups, setBizGroups] = useState([]); // [{key,label,metrics:[{key,label,type}]}]
  const [newBizMetric, setNewBizMetric] = useState({}); // {groupKey: {label,type}}

  // Monitoring alert recipients + client report address
  const [alertEditing, setAlertEditing] = useState(false);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertEmails, setAlertEmails] = useState([]);
  const [clientEmail, setClientEmail] = useState('');
  const [newAlertEmail, setNewAlertEmail] = useState('');

  // Properties selection
  const [showProperties, setShowProperties] = useState(false);
  const [gscProps, setGscProps] = useState([]);
  const [ga4Props, setGa4Props] = useState([]);
  const [selectedGsc, setSelectedGsc] = useState('');
  const [selectedGa4, setSelectedGa4] = useState('');
  const [gscSearch, setGscSearch] = useState('');
  const [ga4Search, setGa4Search] = useState('');
  const [propsLoading, setPropsLoading] = useState(false);
  const [propsSaving, setPropsSaving] = useState(false);
  const [localeOptions, setLocaleOptions] = useState({ countries: [], languages: [] });

  useEffect(() => { loadData(); }, [id]);

  async function loadData() {
    try {
      const [s, gs, cat, loc] = await Promise.all([
        api.getSite(id),
        api.getGoogleStatus(id).catch(() => ({ connected: false })),
        api.getPaidAdsCatalog().catch(() => ({ site_types: [], channels: [] })),
        api.getSiteLocaleOptions().catch(() => ({ countries: [], languages: [] })),
      ]);
      setLocaleOptions(loc);
      setSite(s);
      setGoogleStatus(gs);
      setPaidCatalog(cat);
      setPaidForm({ site_type: s.site_type || '', paid_channels: s.paid_channels || [] });
      setCustomMetrics(s.paid_custom_metrics || {});
      setBizGroups(s.business_groups || []);
      setAlertEmails(s.alert_emails || []);
      setClientEmail(s.client_email || '');
      setForm(detailsForm(s));
      setWpForm({ wp_api_url: s.wp_api_url || '', wp_username: s.wp_username || '', wp_app_password: '' });
      setSelectedGsc(s.gsc_property || '');
      setSelectedGa4(s.ga4_property_id || '');
      setProfileForm({
        business_description: s.business_description || '', business_niche: s.business_niche || '',
        service_area: s.service_area || '', target_audience: s.target_audience || '',
        usp: s.usp || '', writing_style: s.writing_style || '', writing_tone: s.writing_tone || '',
        always_include_words: s.always_include_words || '', never_use_words: s.never_use_words || '',
        brand_color_primary: s.brand_color_primary || '#3B82F6', brand_color_secondary: s.brand_color_secondary || '#22C55E',
      });
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  function detailsForm(s) {
    return { name: s.name, url: s.url, assigned_to: s.assigned_to || '', status: s.status || 'active',
             country: s.country || 'IL', language: s.language || 'he',
             location_code: s.location_code || null, location_name: s.location_name || null };
  }

  function localeLabel(list, code) {
    const opt = (list || []).find(o => o.code === code);
    return opt ? opt.label : code;
  }

  async function checkHealth() {
    setChecking(true);
    try { setHealth(await api.checkSiteHealth(id)); }
    catch { setHealth({ wordpress_ok: false, wordpress_error: 'Failed' }); }
    setChecking(false);
  }

  async function handleGoogleConnect() {
    try { const data = await api.getGoogleLoginUrl(id); window.location.href = data.auth_url; }
    catch (err) { alert(err.message); }
  }

  async function handleGoogleDisconnect() {
    if (!window.confirm('לנתק את חיבור Google?')) return;
    try { await api.disconnectGoogle(id); setGoogleStatus({ connected: false }); setShowProperties(false); setGscProps([]); setGa4Props([]); }
    catch (err) { alert(err.message); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.updateSite(id, form);
      setSite({ ...site, ...form });
      setEditing(false);
      setMessage({ type: 'success', text: 'נשמר בהצלחה' });
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  }

  function isValidEmail(e) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
  }

  function flashError(text) {
    setMessage({ type: 'error', text: text });
    setTimeout(() => setMessage(null), 3000);
  }

  function addAlertEmail() {
    const e = (newAlertEmail || '').trim().toLowerCase();
    if (!e) return;
    if (!isValidEmail(e)) { flashError('כתובת אימייל לא תקינה'); return; }
    if (alertEmails.includes(e)) { flashError('הכתובת כבר קיימת'); return; }
    setAlertEmails([...alertEmails, e]);
    setNewAlertEmail('');
  }

  function removeAlertEmail(e) {
    setAlertEmails(alertEmails.filter(x => x !== e));
  }

  function cancelAlertsEdit() {
    setAlertEditing(false);
    setAlertEmails(site.alert_emails || []);
    setClientEmail(site.client_email || '');
    setNewAlertEmail('');
  }

  async function handleAlertsSave() {
    const client = (clientEmail || '').trim().toLowerCase();
    if (client && !isValidEmail(client)) { flashError('מייל לקוח לא תקין'); return; }
    // A half-typed address left in the "add" box is easy to miss — fold it in
    // rather than silently dropping it on save.
    const pending = (newAlertEmail || '').trim().toLowerCase();
    if (pending && !isValidEmail(pending)) { flashError('כתובת אימייל לא תקינה'); return; }
    const emails = pending && !alertEmails.includes(pending) ? [...alertEmails, pending] : alertEmails;

    setAlertSaving(true);
    try {
      const payload = { client_email: client || null, alert_emails: emails };
      await api.updateSite(id, payload);
      setSite({ ...site, ...payload });
      setAlertEmails(emails);
      setClientEmail(client);
      setNewAlertEmail('');
      setAlertEditing(false);
      setMessage({ type: 'success', text: 'נשמר בהצלחה' });
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); }
    setAlertSaving(false);
    setTimeout(() => setMessage(null), 3000);
  }

  function togglePaidChannel(platform) {
    const cur = paidForm.paid_channels || [];
    const next = cur.includes(platform) ? cur.filter(p => p !== platform) : [...cur, platform];
    setPaidForm({ ...paidForm, paid_channels: next });
  }

  async function handlePaidSave() {
    setPaidSaving(true);
    try {
      await api.updateSite(id, { site_type: paidForm.site_type || null, paid_channels: paidForm.paid_channels || [] });
      setSite({ ...site, site_type: paidForm.site_type, paid_channels: paidForm.paid_channels });
      setPaidEditing(false);
      setMessage({ type: 'success', text: 'נשמר בהצלחה' });
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); }
    setPaidSaving(false);
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleAddCustomMetric(platform) {
    const draft = newMetric[platform] || {};
    if (!draft.label) return;
    try {
      const res = await api.addPaidCustomMetric(id, platform, draft.label, draft.type || 'number');
      setCustomMetrics({ ...customMetrics, [platform]: res.metrics });
      setNewMetric({ ...newMetric, [platform]: { label: '', type: 'number' } });
    } catch (err) { alert(err.message); }
  }

  async function handleDeleteCustomMetric(platform, key) {
    try {
      const res = await api.deletePaidCustomMetric(id, platform, key);
      setCustomMetrics({ ...customMetrics, [platform]: res.metrics });
    } catch (err) { alert(err.message); }
  }

  // ── Business results groups ──
  function nextKey(prefix, existing) {
    var set = new Set(existing || []);
    var n = 1;
    while (set.has(prefix + n)) n++;
    return prefix + n;
  }

  function addBizGroup(template) {
    var key = nextKey('biz_', bizGroups.map(g => g.key));
    var group = template
      ? { key: key, label: template.label, metrics: (template.metrics || []).map(m => ({ ...m })) }
      : { key: key, label: 'קבוצה חדשה', metrics: [] };
    setBizGroups([...bizGroups, group]);
  }

  function removeBizGroup(key) {
    setBizGroups(bizGroups.filter(g => g.key !== key));
  }

  function setBizGroupLabel(key, label) {
    setBizGroups(bizGroups.map(g => g.key === key ? { ...g, label } : g));
  }

  function addBizMetric(groupKey) {
    var draft = newBizMetric[groupKey] || {};
    if (!draft.label) return;
    setBizGroups(bizGroups.map(g => {
      if (g.key !== groupKey) return g;
      var mkey = nextKey('m_', (g.metrics || []).map(m => m.key));
      return { ...g, metrics: [...(g.metrics || []), { key: mkey, label: draft.label, type: draft.type || 'number' }] };
    }));
    setNewBizMetric({ ...newBizMetric, [groupKey]: { label: '', type: 'number' } });
  }

  function removeBizMetric(groupKey, metricKey) {
    setBizGroups(bizGroups.map(g => g.key === groupKey
      ? { ...g, metrics: (g.metrics || []).filter(m => m.key !== metricKey) }
      : g));
  }

  async function handleBizSave() {
    setBizSaving(true);
    try {
      await api.updateSite(id, { business_groups: bizGroups });
      setSite({ ...site, business_groups: bizGroups });
      setBizEditing(false);
      setMessage({ type: 'success', text: 'נשמר בהצלחה' });
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); }
    setBizSaving(false);
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleWpSave() {
    setWpSaving(true);
    try {
      const data = {};
      if (wpForm.wp_api_url) data.wp_api_url = wpForm.wp_api_url;
      if (wpForm.wp_username) data.wp_username = wpForm.wp_username;
      if (wpForm.wp_app_password) data.wp_app_password = wpForm.wp_app_password;
      await api.updateSite(id, data);
      setSite({ ...site, ...data });
      setWpEditing(false);
      setMessage({ type: 'success', text: 'פרטי WordPress נשמרו' });
      checkHealth();
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); }
    setWpSaving(false);
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleDelete() {
    if (!window.confirm('למחוק את האתר? פעולה זו לא ניתנת לביטול')) return;
    try { await api.deleteSite(id); navigate('/'); }
    catch (err) { alert(err.message); }
  }

  async function loadProperties() {
    setPropsLoading(true);
    setShowProperties(true);
    try { const gsc = await api.getGscProperties(id); setGscProps(gsc.properties || []); } catch {}
    try { const ga4 = await api.getGa4Properties(id); setGa4Props(ga4.properties || []); } catch {}
    setPropsLoading(false);
  }

  async function handleSaveProperties() {
    setPropsSaving(true);
    try {
      await api.selectProperties(id, { gsc_property: selectedGsc, ga4_property_id: selectedGa4 });
      setSite({ ...site, gsc_property: selectedGsc, ga4_property_id: selectedGa4 });
      setShowProperties(false);
      setMessage({ type: 'success', text: 'Properties נשמרו בהצלחה' });
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); }
    setPropsSaving(false);
    setTimeout(() => setMessage(null), 3000);
  }

  const filteredGsc = gscProps.filter(p => p.url.toLowerCase().includes(gscSearch.toLowerCase()));
  const filteredGa4 = ga4Props.filter(p =>
    p.display_name.toLowerCase().includes(ga4Search.toLowerCase()) ||
    p.property_id.includes(ga4Search) ||
    p.account.toLowerCase().includes(ga4Search.toLowerCase())
  );

  const msgColors = { success: { bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', text: 'var(--green)' }, error: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: 'var(--red)' } };
  const cardStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', marginBottom: '1rem' };
  const labelStyle = { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' };
  const valueStyle = { fontSize: '13px', color: 'var(--text)', fontFamily: 'var(--mono)', direction: 'ltr', textAlign: 'right' };
  const inputStyle = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '13px', direction: 'ltr' };
  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' };
  const btnStyle = (bg, color, border) => ({ background: bg, color, border: border || 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: 'Heebo,sans-serif' });

  if (loading) return <div style={{padding:'2rem', color:'var(--text-muted)'}}>טוען...</div>;
  if (!site) return <div style={{padding:'2rem', color:'var(--text-muted)'}}>אתר לא נמצא</div>;

  return (
    <>
      <div style={{marginBottom:'1.5rem'}}>
        <h2 style={{fontSize:'20px', fontWeight:700, marginBottom:'4px'}}>הגדרות</h2>
        <p style={{color:'var(--text-secondary)', fontSize:'13px'}}>{site.name}</p>
      </div>

      {message && (
        <div style={{ background: msgColors[message.type]?.bg, border: '1px solid ' + msgColors[message.type]?.border, borderRadius: '8px', padding: '10px 16px', marginBottom: '1rem', fontSize: '13px', color: msgColors[message.type]?.text, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} style={{background:'none', border:'none', color:'inherit', cursor:'pointer', fontSize:'16px'}}>×</button>
        </div>
      )}

      {/* פרטי האתר */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>פרטי האתר</div>
          {!editing ? (
            <button onClick={() => setEditing(true)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ערוך</button>
          ) : (
            <div style={{display:'flex', gap:'6px'}}>
              <button onClick={handleSave} disabled={saving} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600, opacity:saving?0.5:1}}>{saving ? 'שומר...' : 'שמור'}</button>
              <button onClick={() => { setEditing(false); setForm(detailsForm(site)); }} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ביטול</button>
            </div>
          )}
        </div>
        {!editing ? (
          <div>
            <div style={rowStyle}><span style={labelStyle}>שם</span><span style={{fontSize:'13px', color:'var(--text)'}}>{site.name}</span></div>
            <div style={rowStyle}><span style={labelStyle}>כתובת</span><span style={valueStyle}>{site.url}</span></div>
            <div style={rowStyle}><span style={labelStyle}>מקדם</span><span style={{fontSize:'13px', color: site.assigned_to ? 'var(--accent)' : 'var(--text-muted)'}}>{site.assigned_to || 'לא מוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>שוק חיפוש</span><span style={{fontSize:'13px', color:'var(--text)'}}>{localeLabel(localeOptions.countries, site.country || 'IL')}{site.location_name ? ' · ' + locationLabel(site.location_name) : ''} · {localeLabel(localeOptions.languages, site.language || 'he')}</span></div>
            <div style={{...rowStyle, borderBottom:'none'}}><span style={labelStyle}>סטטוס</span><span style={{fontSize:'13px', color: site.status === 'active' ? 'var(--green)' : site.status === 'frozen' ? 'var(--text-muted)' : 'var(--orange)', fontWeight:600}}>{site.status === 'active' ? 'פעיל' : site.status === 'frozen' ? 'מוקפא' : 'מושהה'}</span></div>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            <div><label style={labelStyle}>שם</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={{...inputStyle, direction:'rtl'}} /></div>
            <div><label style={labelStyle}>כתובת</label><input value={form.url} onChange={e => setForm({...form, url: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>מקדם</label><input value={form.assigned_to || ''} onChange={e => setForm({...form, assigned_to: e.target.value})} style={{...inputStyle, direction:'rtl'}} placeholder="שם המקדם..." /></div>
            <div><label style={labelStyle}>סטטוס</label><select value={form.status || 'active'} onChange={e => setForm({...form, status: e.target.value})} style={{...inputStyle, direction:'rtl'}}><option value="active">פעיל</option><option value="paused">מושהה</option><option value="frozen">מוקפא</option></select></div>
            <div style={{display:'flex', gap:'10px'}}>
              <div style={{flex:1}}><label style={labelStyle}>מדינת חיפוש</label><select value={form.country || 'IL'} onChange={e => setForm({...form, country: e.target.value, location_code: null, location_name: null})} style={{...inputStyle, direction:'rtl'}}>{localeOptions.countries.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}</select></div>
              <div style={{flex:1}}><label style={labelStyle}>שפת חיפוש</label><select value={form.language || 'he'} onChange={e => setForm({...form, language: e.target.value})} style={{...inputStyle, direction:'rtl'}}>{localeOptions.languages.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}</select></div>
            </div>
            <div>
              <label style={labelStyle}>מיקום ברירת מחדל (עיר / מדינה בתוך המדינה)</label>
              {form.country === (site.country || 'IL') ? (
                <LocationPicker siteId={id} value={form.location_name}
                  onSelect={loc => setForm({...form, location_code: loc.code, location_name: loc.name})}
                  onClear={() => setForm({...form, location_code: null, location_name: null})}
                  clearLabel="כל המדינה" placeholder="כל המדינה — חפש עיר או מדינה..." />
              ) : (
                <div style={{fontSize:'12px', color:'var(--text-muted)'}}>שמור קודם את המדינה החדשה, ואז אפשר לבחור מיקום בתוכה</div>
              )}
              <div style={{fontSize:'11px', color:'var(--text-muted)', marginTop:'4px'}}>ביטויים בלי מיקום משלהם נבדקים מכאן. מיקום לביטוי מסוים נקבע בעמוד הביטויים.</div>
            </div>
            {(form.country !== (site.country || 'IL') || form.language !== (site.language || 'he') || (form.location_code || null) !== (site.location_code || null)) && (
              <div style={{fontSize:'12px', color:'var(--orange)'}}>מכאן והלאה המיקומים, נפחי החיפוש ונתוני Search Console יימדדו בשוק החדש. ההיסטוריה הקיימת נמדדה בשוק הקודם ולא תהיה ברת השוואה.</div>
            )}
          </div>
        )}
      </div>

      {/* התראות ניטור */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>התראות ניטור</div>
          {!alertEditing ? (
            <button onClick={() => setAlertEditing(true)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ערוך</button>
          ) : (
            <div style={{display:'flex', gap:'6px'}}>
              <button onClick={handleAlertsSave} disabled={alertSaving} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600, opacity:alertSaving?0.5:1}}>{alertSaving ? 'שומר...' : 'שמור'}</button>
              <button onClick={cancelAlertsEdit} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ביטול</button>
            </div>
          )}
        </div>
        <div style={{fontSize:'12px', color:'var(--text-muted)', marginBottom:'12px'}}>
          התראות נפילת אתר, תפוגת SSL ותפוגת דומיין יישלחו לכתובות אלו, בנוסף לכתובת המערכת.
        </div>
        {!alertEditing ? (
          <div>
            <div style={rowStyle}>
              <span style={labelStyle}>מייל לקוח (לשליחת דוחות)</span>
              <span style={site.client_email ? valueStyle : {fontSize:'13px', color:'var(--text-muted)'}}>{site.client_email || 'לא מוגדר'}</span>
            </div>
            <div style={{...rowStyle, borderBottom:'none'}}>
              <span style={labelStyle}>מיילים נוספים להתראות</span>
              <span style={(site.alert_emails||[]).length ? valueStyle : {fontSize:'13px', color:'var(--text-muted)'}}>
                {(site.alert_emails||[]).length ? (site.alert_emails||[]).join(', ') : 'לא מוגדר'}
              </span>
            </div>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'14px'}}>
            <div>
              <label style={labelStyle}>מייל לקוח (לשליחת דוחות)</label>
              <input type="email" value={clientEmail} placeholder="client@example.com" onChange={e => setClientEmail(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>מיילים נוספים להתראות</label>
              {alertEmails.length > 0 ? (
                <div style={{display:'flex', gap:'8px', flexWrap:'wrap', margin:'6px 0'}}>
                  {alertEmails.map(e => (
                    <span key={e} style={{display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 10px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'16px', fontSize:'12px', fontFamily:'var(--mono)', direction:'ltr'}}>
                      {e}
                      <button onClick={() => removeAlertEmail(e)} title="הסר" style={{border:'none', background:'none', color:'var(--red)', cursor:'pointer', fontSize:'14px', lineHeight:1, padding:0}}>×</button>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{fontSize:'12px', color:'var(--text-muted)', padding:'8px 12px', background:'var(--bg)', border:'1px dashed var(--border)', borderRadius:'6px', margin:'6px 0'}}>
                  אין כתובות נוספות — ההתראות יישלחו רק לכתובת המערכת.
                </div>
              )}
              <div style={{display:'flex', gap:'8px'}}>
                <input
                  type="email" value={newAlertEmail} placeholder="name@example.com"
                  onChange={e => setNewAlertEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlertEmail(); } }}
                  style={{...inputStyle, flex:1}}
                />
                <button onClick={addAlertEmail} style={{...btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)'), whiteSpace:'nowrap'}}>+ הוסף</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* פרסום ממומן */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>סוג אתר ופרסום ממומן</div>
          {!paidEditing ? (
            <button onClick={() => setPaidEditing(true)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ערוך</button>
          ) : (
            <div style={{display:'flex', gap:'6px'}}>
              <button onClick={handlePaidSave} disabled={paidSaving} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600, opacity:paidSaving?0.5:1}}>{paidSaving ? 'שומר...' : 'שמור'}</button>
              <button onClick={() => { setPaidEditing(false); setPaidForm({ site_type: site.site_type || '', paid_channels: site.paid_channels || [] }); }} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ביטול</button>
            </div>
          )}
        </div>
        {!paidEditing ? (
          <div>
            <div style={rowStyle}>
              <span style={labelStyle}>סוג אתר</span>
              <span style={{fontSize:'13px', color: site.site_type ? 'var(--text)' : 'var(--text-muted)'}}>
                {(paidCatalog.site_types.find(t => t.key === site.site_type) || {}).label || 'לא מוגדר'}
              </span>
            </div>
            <div style={{...rowStyle, borderBottom:'none'}}>
              <span style={labelStyle}>ערוצי פרסום</span>
              <span style={{fontSize:'13px', color: (site.paid_channels||[]).length ? 'var(--accent)' : 'var(--text-muted)', textAlign:'left'}}>
                {(site.paid_channels||[]).length
                  ? (site.paid_channels||[]).map(p => (paidCatalog.channels.find(c => c.key === p) || {}).label || p).join(', ')
                  : 'לא מוגדר'}
              </span>
            </div>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'14px'}}>
            <div>
              <label style={labelStyle}>סוג אתר</label>
              <select value={paidForm.site_type || ''} onChange={e => setPaidForm({...paidForm, site_type: e.target.value})} style={{...inputStyle, direction:'rtl'}}>
                <option value="">לא מוגדר</option>
                {paidCatalog.site_types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>ערוצי פרסום ממומן</label>
              <div style={{display:'flex', flexDirection:'column', gap:'6px', marginTop:'4px'}}>
                {paidCatalog.channels.map(c => (
                  <label key={c.key} style={{display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', cursor:'pointer'}}>
                    <input type="checkbox" checked={(paidForm.paid_channels||[]).includes(c.key)} onChange={() => togglePaidChannel(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            {(paidForm.paid_channels||[]).map(platform => {
              const ch = paidCatalog.channels.find(c => c.key === platform) || { label: platform };
              const customs = customMetrics[platform] || [];
              const draft = newMetric[platform] || { label: '', type: 'number' };
              return (
                <div key={platform} style={{border:'1px solid var(--border)', borderRadius:'8px', padding:'10px'}}>
                  <div style={{fontSize:'12px', fontWeight:600, marginBottom:'6px'}}>מדדים מותאמים — {ch.label}</div>
                  {customs.length > 0 && (
                    <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'8px'}}>
                      {customs.map(m => (
                        <span key={m.key} style={{display:'inline-flex', alignItems:'center', gap:'6px', fontSize:'12px', background:'var(--surface-hover)', border:'1px solid var(--border)', borderRadius:'6px', padding:'2px 8px'}}>
                          {m.label}
                          <button onClick={() => handleDeleteCustomMetric(platform, m.key)} style={{background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:'14px', lineHeight:1}}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{display:'flex', gap:'6px'}}>
                    <input value={draft.label} onChange={e => setNewMetric({...newMetric, [platform]: {...draft, label: e.target.value}})} placeholder="שם מדד..." style={{...inputStyle, direction:'rtl', flex:1}} />
                    <select value={draft.type || 'number'} onChange={e => setNewMetric({...newMetric, [platform]: {...draft, type: e.target.value}})} style={{...inputStyle, width:'110px'}}>
                      <option value="number">מספר</option>
                      <option value="currency">מטבע (₪)</option>
                      <option value="percent">אחוז (%)</option>
                    </select>
                    <button onClick={() => handleAddCustomMetric(platform)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>הוסף</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* תוצאות עסקיות */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>תוצאות עסקיות</div>
          {!bizEditing ? (
            <button onClick={() => setBizEditing(true)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ערוך</button>
          ) : (
            <div style={{display:'flex', gap:'6px'}}>
              <button onClick={handleBizSave} disabled={bizSaving} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600, opacity:bizSaving?0.5:1}}>{bizSaving ? 'שומר...' : 'שמור'}</button>
              <button onClick={() => { setBizEditing(false); setBizGroups(site.business_groups || []); }} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ביטול</button>
            </div>
          )}
        </div>
        <div style={{fontSize:'12px', color:'var(--text-muted)', marginBottom:'10px'}}>
          קבוצות מדדים חודשיות (כמו "דוח לידים" / "דוח תנועה"). המדדים מוזנים בכל חודש במסך הדוחות ומוצגים במודול "תוצאות עסקיות".
        </div>
        {!bizEditing ? (
          <div>
            {(site.business_groups || []).length === 0
              ? <div style={{fontSize:'13px', color:'var(--text-muted)'}}>לא הוגדרו קבוצות.</div>
              : (site.business_groups || []).map(g => (
                  <div key={g.key} style={{...rowStyle, borderBottom:'1px solid var(--border)'}}>
                    <span style={labelStyle}>{g.label}</span>
                    <span style={{fontSize:'13px', color:'var(--text-muted)'}}>{(g.metrics||[]).map(m => m.label).join(' · ') || 'ללא מדדים'}</span>
                  </div>
                ))}
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'14px'}}>
            {bizGroups.map(g => {
              const draft = newBizMetric[g.key] || { label: '', type: 'number' };
              return (
                <div key={g.key} style={{border:'1px solid var(--border)', borderRadius:'8px', padding:'12px'}}>
                  <div style={{display:'flex', gap:'8px', alignItems:'center', marginBottom:'8px'}}>
                    <input value={g.label} onChange={e => setBizGroupLabel(g.key, e.target.value)} style={{...inputStyle, direction:'rtl', flex:1, fontWeight:600}} placeholder="שם הקבוצה..." />
                    <button onClick={() => removeBizGroup(g.key)} style={btnStyle('transparent', 'var(--red)', '1px solid rgba(239,68,68,0.3)')}>מחק קבוצה</button>
                  </div>
                  {(g.metrics||[]).length > 0 && (
                    <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'8px'}}>
                      {g.metrics.map(m => (
                        <span key={m.key} style={{display:'inline-flex', alignItems:'center', gap:'6px', fontSize:'12px', background:'var(--surface-hover)', border:'1px solid var(--border)', borderRadius:'6px', padding:'2px 8px'}}>
                          {m.label}{m.type !== 'number' ? (m.type === 'currency' ? ' (₪)' : ' (%)') : ''}
                          <button onClick={() => removeBizMetric(g.key, m.key)} style={{background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:'14px', lineHeight:1}}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{display:'flex', gap:'6px'}}>
                    <input value={draft.label} onChange={e => setNewBizMetric({...newBizMetric, [g.key]: {...draft, label: e.target.value}})} placeholder="שם מדד..." style={{...inputStyle, direction:'rtl', flex:1}} />
                    <select value={draft.type || 'number'} onChange={e => setNewBizMetric({...newBizMetric, [g.key]: {...draft, type: e.target.value}})} style={{...inputStyle, width:'110px'}}>
                      <option value="number">מספר</option>
                      <option value="currency">מטבע (₪)</option>
                      <option value="percent">אחוז (%)</option>
                    </select>
                    <button onClick={() => addBizMetric(g.key)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>הוסף</button>
                  </div>
                </div>
              );
            })}
            <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
              <button onClick={() => addBizGroup(null)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>+ קבוצה ריקה</button>
              {((paidCatalog.business_defaults && paidCatalog.business_defaults[paidForm.site_type]) || []).map((tpl, i) => (
                <button key={i} onClick={() => addBizGroup(tpl)} style={btnStyle('var(--surface-hover)', 'var(--accent)', '1px solid var(--border)')}>+ תבנית: {tpl.label}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* חיבור WordPress */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>חיבור WordPress</div>
          <div style={{display:'flex', gap:'6px'}}>
            {!wpEditing ? (
              <button onClick={() => setWpEditing(true)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>
                {site.wp_api_url ? 'ערוך חיבור' : 'הגדר חיבור'}
              </button>
            ) : (
              <>
                <button onClick={handleWpSave} disabled={wpSaving} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600, opacity:wpSaving?0.5:1}}>{wpSaving ? 'שומר...' : 'שמור'}</button>
                <button onClick={() => { setWpEditing(false); setWpForm({ wp_api_url: site.wp_api_url || '', wp_username: site.wp_username || '', wp_app_password: '' }); }} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ביטול</button>
              </>
            )}
            <button onClick={checkHealth} disabled={checking} style={{...btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)'), opacity:checking?0.5:1}}>{checking ? 'בודק...' : 'בדיקת חיבור'}</button>
          </div>
        </div>

        {!wpEditing ? (
          <div>
            <div style={rowStyle}><span style={labelStyle}>API URL</span><span style={valueStyle}>{site.wp_api_url || 'לא מוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>Username</span><span style={valueStyle}>{site.wp_username || 'לא מוגדר'}</span></div>
            <div style={{...rowStyle, borderBottom:'none'}}><span style={labelStyle}>App Password</span><span style={{fontSize:'13px', color: site.wp_api_url ? 'var(--green)' : 'var(--text-muted)'}}>{site.wp_api_url ? '••••••••' : 'לא מוגדר'}</span></div>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            <div>
              <label style={labelStyle}>WP REST API URL</label>
              <input value={wpForm.wp_api_url} onChange={e => setWpForm({...wpForm, wp_api_url: e.target.value})} placeholder="https://example.co.il/wp-json/wp/v2" style={inputStyle} />
              <div style={{fontSize:'10px', color:'var(--text-muted)', marginTop:'2px'}}>בדרך כלל: https://yourdomain.com/wp-json/wp/v2</div>
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input value={wpForm.wp_username} onChange={e => setWpForm({...wpForm, wp_username: e.target.value})} placeholder="admin" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Application Password</label>
              <input value={wpForm.wp_app_password} onChange={e => setWpForm({...wpForm, wp_app_password: e.target.value})} placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" style={inputStyle} type="password" />
              <div style={{fontSize:'10px', color:'var(--text-muted)', marginTop:'2px'}}>WordPress &gt; Users &gt; Application Passwords</div>
            </div>
          </div>
        )}

        {health && (
          <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'6px', background: health.wordpress_ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border:'1px solid ' + (health.wordpress_ok ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)') }}>
            <span style={{color: health.wordpress_ok ? 'var(--green)' : 'var(--red)', fontSize:'13px', fontWeight:500}}>
              {health.wordpress_ok ? '✓ החיבור תקין' : '✗ שגיאה: ' + health.wordpress_error}
            </span>
          </div>
        )}
      </div>

      {/* חיבור Google */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>חיבור Google APIs</div>
          {googleStatus?.connected ? (
            <div style={{display:'flex', gap:'6px', alignItems:'center'}}>
              <span style={{fontSize:'11px', color:'var(--green)'}}>✓ מחובר {googleStatus.connected_by ? '(' + googleStatus.connected_by + ')' : ''}</span>
              <button onClick={handleGoogleDisconnect} style={{...btnStyle('transparent', 'var(--red)', '1px solid rgba(239,68,68,0.3)'), fontSize:'11px'}}>נתק</button>
            </div>
          ) : (
            <button onClick={handleGoogleConnect} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600}}>חבר Google</button>
          )}
        </div>

        <div>
          <div style={rowStyle}>
            <span style={labelStyle}>Search Console</span>
            <span style={{fontSize:'13px', color: site.gsc_property ? 'var(--text)' : 'var(--text-muted)', fontFamily:'var(--mono)', direction:'ltr'}}>{site.gsc_property || 'לא מוגדר'}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Analytics 4</span>
            <span style={{fontSize:'13px', color: site.ga4_property_id ? 'var(--text)' : 'var(--text-muted)', fontFamily:'var(--mono)', direction:'ltr'}}>{site.ga4_property_id || 'לא מוגדר'}</span>
          </div>
        </div>

        {googleStatus?.connected && (
          <div style={{marginTop:'10px'}}>
            <button onClick={loadProperties} disabled={propsLoading} style={{...btnStyle('var(--surface-hover)', 'var(--accent)', '1px solid var(--border)'), opacity:propsLoading?0.5:1}}>
              {propsLoading ? 'טוען...' : showProperties ? 'סגור בחירת Properties' : 'בחר/שנה Properties'}
            </button>
          </div>
        )}

        {/* Properties selection inline */}
        {showProperties && googleStatus?.connected && (
          <div style={{marginTop:'16px', padding:'16px', background:'var(--bg)', borderRadius:'8px', border:'1px solid var(--border)'}}>
            {propsLoading ? (
              <div style={{textAlign:'center', padding:'20px', color:'var(--text-muted)', fontSize:'13px'}}>טוען properties מ-Google...</div>
            ) : (
              <>
                {/* GSC */}
                <div style={{marginBottom:'20px'}}>
                  <div style={{fontSize:'13px', fontWeight:600, marginBottom:'8px'}}>
                    Search Console Property
                    {gscProps.length > 0 && <span style={{color:'var(--text-muted)', fontWeight:400, fontSize:'11px', marginRight:'6px'}}>({gscProps.length})</span>}
                  </div>
                  {gscProps.length > 5 && (
                    <input value={gscSearch} onChange={e => setGscSearch(e.target.value)} placeholder="חיפוש..." style={{...inputStyle, marginBottom:'6px', padding:'6px 10px', fontSize:'12px'}} />
                  )}
                  <div style={{display:'flex', flexDirection:'column', gap:'3px', maxHeight:'200px', overflowY:'auto'}}>
                    {filteredGsc.map(p => (
                      <label key={p.url} onClick={() => setSelectedGsc(p.url)} style={{
                        display:'flex', alignItems:'center', gap:'8px', padding:'6px 10px', borderRadius:'5px', cursor:'pointer',
                        border: selectedGsc === p.url ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: selectedGsc === p.url ? 'rgba(59,130,246,0.08)' : 'transparent', transition:'all 0.15s',
                      }}>
                        <input type="radio" name="gsc" checked={selectedGsc === p.url} readOnly style={{accentColor:'var(--accent)'}} />
                        <span style={{direction:'ltr', fontFamily:'var(--mono)', fontSize:'11px', flex:1}}>{p.url}</span>
                        <span style={{color:'var(--text-muted)', fontSize:'9px'}}>{p.permission}</span>
                      </label>
                    ))}
                    {gscProps.length === 0 && <div style={{color:'var(--text-muted)', fontSize:'12px', padding:'8px'}}>לא נמצאו properties</div>}
                  </div>
                </div>

                {/* GA4 */}
                <div style={{marginBottom:'16px'}}>
                  <div style={{fontSize:'13px', fontWeight:600, marginBottom:'8px'}}>
                    Google Analytics 4 Property
                    {ga4Props.length > 0 && <span style={{color:'var(--text-muted)', fontWeight:400, fontSize:'11px', marginRight:'6px'}}>({ga4Props.length})</span>}
                  </div>
                  {ga4Props.length > 5 && (
                    <input value={ga4Search} onChange={e => setGa4Search(e.target.value)} placeholder="חיפוש..." style={{...inputStyle, marginBottom:'6px', padding:'6px 10px', fontSize:'12px'}} />
                  )}
                  <div style={{display:'flex', flexDirection:'column', gap:'3px', maxHeight:'200px', overflowY:'auto'}}>
                    {filteredGa4.map(p => (
                      <label key={p.property_id} onClick={() => setSelectedGa4(p.property_id)} style={{
                        display:'flex', alignItems:'center', gap:'8px', padding:'6px 10px', borderRadius:'5px', cursor:'pointer',
                        border: selectedGa4 === p.property_id ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: selectedGa4 === p.property_id ? 'rgba(59,130,246,0.08)' : 'transparent', transition:'all 0.15s',
                      }}>
                        <input type="radio" name="ga4" checked={selectedGa4 === p.property_id} readOnly style={{accentColor:'var(--accent)'}} />
                        <div>
                          <div style={{fontSize:'12px', fontWeight:600}}>{p.display_name}</div>
                          <div style={{color:'var(--text-muted)', fontSize:'10px', direction:'ltr'}}>{p.account} / {p.property_id}</div>
                        </div>
                      </label>
                    ))}
                    {ga4Props.length === 0 && <div style={{color:'var(--text-muted)', fontSize:'12px', padding:'8px'}}>לא נמצאו GA4 properties</div>}
                  </div>
                </div>

                {/* Save button */}
                <div style={{display:'flex', gap:'8px'}}>
                  <button onClick={handleSaveProperties} disabled={propsSaving} style={{...btnStyle('var(--accent)', '#fff'), fontWeight:600, padding:'7px 18px', opacity:propsSaving?0.5:1}}>
                    {propsSaving ? 'שומר...' : 'שמור Properties'}
                  </button>
                  <button onClick={() => setShowProperties(false)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>סגור</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* פרופיל תוכן */}
      <div style={cardStyle}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <div style={{fontSize:'14px', fontWeight:600}}>פרופיל תוכן & מותג</div>
          {!profileEditing ? (
            <button onClick={() => setProfileEditing(true)} style={btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)')}>ערוך</button>
          ) : (
            <div style={{display:'flex', gap:'6px'}}>
              <button onClick={async () => {
                setProfileSaving(true);
                try {
                  await api.updateSite(id, profileForm);
                  setSite({...site, ...profileForm});
                  setProfileEditing(false);
                  setMessage({type:'success', text:'פרופיל נשמר'});
                } catch(e) { setMessage({type:'error', text:e.message}); }
                setProfileSaving(false);
                setTimeout(() => setMessage(null), 3000);
              }} disabled={profileSaving} style={{...btnStyle('var(--accent)','#fff'), fontWeight:600, opacity:profileSaving?0.5:1}}>{profileSaving?'שומר...':'שמור'}</button>
              <button onClick={() => setProfileEditing(false)} style={btnStyle('var(--surface-hover)','var(--text)','1px solid var(--border)')}>ביטול</button>
            </div>
          )}
        </div>

        {!profileEditing ? (
          <div>
            <div style={rowStyle}><span style={labelStyle}>תחום/נישה</span><span style={{fontSize:'13px', color: site.business_niche ? 'var(--text)' : 'var(--text-muted)'}}>{site.business_niche || 'לא הוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>אזור שירות</span><span style={{fontSize:'13px', color: site.service_area ? 'var(--text)' : 'var(--text-muted)'}}>{site.service_area || 'לא הוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>קהל יעד</span><span style={{fontSize:'13px', color: site.target_audience ? 'var(--text)' : 'var(--text-muted)'}}>{site.target_audience || 'לא הוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>סגנון כתיבה</span><span style={{fontSize:'13px', color: site.writing_style ? 'var(--text)' : 'var(--text-muted)'}}>{site.writing_style || 'לא הוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>טון</span><span style={{fontSize:'13px', color: site.writing_tone ? 'var(--text)' : 'var(--text-muted)'}}>{site.writing_tone || 'לא הוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>תיאור העסק</span><span style={{fontSize:'13px', color: site.business_description ? 'var(--text)' : 'var(--text-muted)', maxWidth:'60%', textAlign:'left'}}>{site.business_description || 'לא הוגדר'}</span></div>
            <div style={rowStyle}><span style={labelStyle}>USP</span><span style={{fontSize:'13px', color: site.usp ? 'var(--text)' : 'var(--text-muted)', maxWidth:'60%', textAlign:'left'}}>{site.usp || 'לא הוגדר'}</span></div>
            <div style={rowStyle}>
              <span style={labelStyle}>צבעי מותג</span>
              <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                {site.brand_color_primary && <div style={{width:'24px', height:'24px', borderRadius:'4px', background: site.brand_color_primary, border:'1px solid var(--border)'}} title="ראשי" />}
                {site.brand_color_secondary && <div style={{width:'24px', height:'24px', borderRadius:'4px', background: site.brand_color_secondary, border:'1px solid var(--border)'}} title="משני" />}
                {!site.brand_color_primary && <span style={{fontSize:'13px', color:'var(--text-muted)'}}>לא הוגדר</span>}
              </div>
            </div>
            <div style={{...rowStyle, borderBottom:'none'}}>
              <span style={labelStyle}>לוגו</span>
              {site.logo_url ? (
                <img src={site.logo_url} alt="logo" style={{maxHeight:'40px', maxWidth:'120px', borderRadius:'4px'}} />
              ) : (
                <span style={{fontSize:'13px', color:'var(--text-muted)'}}>לא הועלה</span>
              )}
            </div>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
              <div><label style={labelStyle}>תחום/נישה</label><input style={{...inputStyle, direction:'rtl'}} value={profileForm.business_niche} onChange={e => setProfileForm({...profileForm, business_niche: e.target.value})} placeholder='גינון, עורכי דין, מרפאת שיניים...' /></div>
              <div><label style={labelStyle}>אזור שירות</label><input style={{...inputStyle, direction:'rtl'}} value={profileForm.service_area} onChange={e => setProfileForm({...profileForm, service_area: e.target.value})} placeholder='תל אביב והמרכז, ארצי...' /></div>
            </div>
            <div><label style={labelStyle}>קהל יעד</label><input style={{...inputStyle, direction:'rtl'}} value={profileForm.target_audience} onChange={e => setProfileForm({...profileForm, target_audience: e.target.value})} placeholder='בעלי בתים, עסקים קטנים, נשים 25-45...' /></div>
            <div><label style={labelStyle}>תיאור העסק (2-3 משפטים)</label><textarea style={{...inputStyle, direction:'rtl', minHeight:'60px', resize:'vertical'}} value={profileForm.business_description} onChange={e => setProfileForm({...profileForm, business_description: e.target.value})} placeholder='מה העסק עושה, מאז מתי, מה מייחד אותו...' /></div>
            <div><label style={labelStyle}>USP - מה מיוחד בעסק</label><input style={{...inputStyle, direction:'rtl'}} value={profileForm.usp} onChange={e => setProfileForm({...profileForm, usp: e.target.value})} placeholder='20 שנות ניסיון, אחריות 5 שנים, שירות 24/7...' /></div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
              <div>
                <label style={labelStyle}>סגנון כתיבה</label>
                <select style={{...inputStyle, direction:'rtl'}} value={profileForm.writing_style} onChange={e => setProfileForm({...profileForm, writing_style: e.target.value})}>
                  <option value="">בחר...</option>
                  <option value="מקצועי">מקצועי</option>
                  <option value="ידידותי">ידידותי</option>
                  <option value="אקדמי">אקדמי</option>
                  <option value="שיווקי">שיווקי</option>
                  <option value="עממי">עממי</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>טון</label>
                <select style={{...inputStyle, direction:'rtl'}} value={profileForm.writing_tone} onChange={e => setProfileForm({...profileForm, writing_tone: e.target.value})}>
                  <option value="">בחר...</option>
                  <option value="רשמי">רשמי</option>
                  <option value="חם">חם</option>
                  <option value="ישיר">ישיר</option>
                  <option value="סמכותי">סמכותי</option>
                  <option value="קליל">קליל</option>
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>מילים לשלב תמיד (מופרדות בפסיקים)</label><input style={{...inputStyle, direction:'rtl'}} value={profileForm.always_include_words} onChange={e => setProfileForm({...profileForm, always_include_words: e.target.value})} placeholder='שם העסק, אזור, מילות מפתח חשובות...' /></div>
            <div><label style={labelStyle}>מילים להימנע מהן (מופרדות בפסיקים)</label><input style={{...inputStyle, direction:'rtl'}} value={profileForm.never_use_words} onChange={e => setProfileForm({...profileForm, never_use_words: e.target.value})} placeholder='מתחרים, מילים שליליות...' /></div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
              <div>
                <label style={labelStyle}>צבע ראשי</label>
                <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                  <input type="color" value={profileForm.brand_color_primary || '#3B82F6'} onChange={e => setProfileForm({...profileForm, brand_color_primary: e.target.value})} style={{width:'40px', height:'32px', border:'none', cursor:'pointer', background:'none'}} />
                  <input style={{...inputStyle, width:'100px', fontFamily:'var(--mono)', fontSize:'12px'}} value={profileForm.brand_color_primary} onChange={e => setProfileForm({...profileForm, brand_color_primary: e.target.value})} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>צבע משני</label>
                <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                  <input type="color" value={profileForm.brand_color_secondary || '#22C55E'} onChange={e => setProfileForm({...profileForm, brand_color_secondary: e.target.value})} style={{width:'40px', height:'32px', border:'none', cursor:'pointer', background:'none'}} />
                  <input style={{...inputStyle, width:'100px', fontFamily:'var(--mono)', fontSize:'12px'}} value={profileForm.brand_color_secondary} onChange={e => setProfileForm({...profileForm, brand_color_secondary: e.target.value})} />
                </div>
              </div>
            </div>
            <div>
              <label style={labelStyle}>לוגו</label>
              <div style={{display:'flex', gap:'12px', alignItems:'center'}}>
                {site.logo_url && <img src={site.logo_url} alt="logo" style={{maxHeight:'40px', maxWidth:'100px', borderRadius:'4px', border:'1px solid var(--border)'}} />}
                <label style={{...btnStyle('var(--surface-hover)', 'var(--text)', '1px solid var(--border)'), cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'4px'}}>
                  {logoUploading ? 'מעלה...' : '📎 העלה לוגו'}
                  <input type="file" accept="image/*" style={{display:'none'}} onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) { setMessage({type:'error', text:'קובץ גדול מדי (מקסימום 2MB)'}); return; }
                    setLogoUploading(true);
                    try {
                      const reader = new FileReader();
                      reader.onload = async () => {
                        const res = await fetch((process.env.PUBLIC_URL || '') + '/api/sites/' + id + '/logo', {
                          method: 'PUT', headers: {'Content-Type':'application/json'},
                          body: JSON.stringify({logo: reader.result})
                        });
                        const data = await res.json();
                        if (data.logo_url) {
                          setSite({...site, logo_url: data.logo_url});
                          setMessage({type:'success', text:'לוגו הועלה'});
                        }
                        setLogoUploading(false);
                      };
                      reader.readAsDataURL(file);
                    } catch(err) { setMessage({type:'error', text:err.message}); setLogoUploading(false); }
                    setTimeout(() => setMessage(null), 3000);
                  }} />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ייבוא מ-ZEFO */}
      <ZefoImport siteId={site.id} siteName={site.name} />

      {/* מחיקת אתר */}
      <div style={{...cardStyle, borderColor:'rgba(239,68,68,0.2)', marginTop:'2rem'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div>
            <div style={{fontSize:'14px', fontWeight:600, color:'var(--red)'}}>אזור מסוכן</div>
            <div style={{fontSize:'12px', color:'var(--text-muted)', marginTop:'2px'}}>מחיקת האתר כולל כל הנתונים</div>
          </div>
          <button onClick={handleDelete} style={{...btnStyle('transparent', 'var(--red)', '1px solid var(--red)'), fontWeight:600}}>מחק אתר</button>
        </div>
      </div>
    </>
  );
}
